import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { toProperCase } from '../../utils/Common';
import { getUser } from '../../utils/Common';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { patchUser, delSharing, getUserForBuilderAdmin } from '../../utils/bedrockAPI';

export default function UserInfoPanel({
  user,
  setUser,
  isMobile,
  shareWithEmail,
  builder,
  history,
  userCaps
}) {
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [createdBy, setCreatedBy] = useState(null);

  useEffect(() => {
    if (user && user.createdBy) {
      getUserForBuilderAdmin(user.createdBy).then(cb => {
        setCreatedBy(cb);
      });
    }
  }, [user]);

  const handleResendActivation = async () => {
    if (isSending) return;

    setIsSending(true);
    try {
      await shareWithEmail(user.email, user.userId);
      setIsSent(true);
    } catch (error) {
      console.error('Failed to send activation email:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleEditStart = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };

  const handleEditSave = async (field) => {
    if (!user) return;

    try {
      await patchUser(user.userId, { [field]: editValue });
      setUser(current => ({ ...current, [field]: editValue }));
    } catch (error) {
      console.error('Error updating user info:', error);
    }

    setEditingField(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleCopySuccess = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      handleEditSave(field);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleDeleteUser = async () => {
    if (window.confirm(
      'Are you sure you want to delete this user?\n\n' +
      'This will:\n' +
      '- Remove access to all cameras\n' +
      '- Remove association with any employee groups\n' +
      '- Mark the user\'s account for permanent deletion\n\n' +
      'This action cannot be undone.'
    )) {
      try {
        const userCapIds = userCaps
          .filter(uc => uc.userId === user.userId)
          .map(uc => uc.userCapId);

        for (const userCapId of userCapIds) {
          await delSharing(userCapId);
        }

        await patchUser(user.userId, {
          builderId: null,
          builderStatus: 'DELETE'
        });

        history.push(`/accesscontrol/${builder.builderId}`);
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const isAdmin = getUser().role === 'ADMIN';

  const renderEditableField = (field, label, placeholder) => (
    <div className="user-info-field">
      <strong>{label}:</strong>
      {editingField === field ? (
        <div className="editable-field-container">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, field)}
            className="editable-field-input"
            placeholder={placeholder}
            autoFocus
          />
          <Button
            className="btn-brand-dark btn-compact editable-field-button"
            onClick={() => handleEditSave(field)}
          >
            Save
          </Button>
          <Button className="btn-brand-outline btn-compact" onClick={handleEditCancel}>
            Cancel
          </Button>
        </div>
      ) : (
        <span
          onClick={() => handleEditStart(field, user[field])}
          className="editable-field"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleEditStart(field, user[field])}
        >
          {field === 'firstName' || field === 'lastName'
            ? (toProperCase(user[field]) || 'Click to add')
            : (user[field] || 'Click to add')}
        </span>
      )}
    </div>
  );

  return (
    <section className="access-control-panel user-info-panel">
      <h2 className="access-control-section-title access-control-section-title--spaced">
        User Information
      </h2>
      <div className="user-info-fields">
        <div className="user-info-field">
          <strong>Email:</strong>
          <div className="email-field-container">
            <span className="non-editable-field">{user.email}</span>
            {isAdmin && (
              <CopyToClipboard text={user.email} onCopy={handleCopySuccess}>
                <Button
                  className="btn-brand-outline btn-compact copy-email-button"
                  title={copySuccess ? 'Copied!' : 'Copy email'}
                >
                  <span className={`glyphicon ${copySuccess ? 'glyphicon-ok' : 'glyphicon-copy'}`} />
                </Button>
              </CopyToClipboard>
            )}
          </div>
        </div>
        {renderEditableField('firstName', 'First Name')}
        {renderEditableField('lastName', 'Last Name')}
        {renderEditableField('phone', 'Phone', '(123) 456-7890')}
        {createdBy && (
          <div className="user-info-field">
            <strong>User Created by:</strong>
            <span className="non-editable-field">{createdBy.email}</span>
          </div>
        )}
      </div>
      {user.role === 'SHARE_CREATE' && (
        <div className="activation-button-container">
          <Button
            className="btn-brand-yellow btn-compact resend-activation-button"
            onClick={handleResendActivation}
            disabled={isSending || isSent}
          >
            {isSending ? 'Sending...' : isSent ? 'Activation Email Sent!' : 'Resend Activation Email'}
          </Button>
        </div>
      )}
      <div className="builder-actions-container">
        <Button
          className={user.builderId === builder.builderId
            ? 'btn-brand-outline btn-compact'
            : 'btn-brand-dark btn-compact'}
          onClick={async () => {
            if (user.builderId === builder.builderId) {
              if (window.confirm('Are you sure you want to remove this user from your Employee Group? This will remove their association with the company but will not remove their camera access.\n\nTo remove their camera access, use the "Update Access to All Cameras" selector on this page or click "Delete User Permanently" below.')) {
                await patchUser(user.userId, { builderId: null });
                history.push(`/accesscontrol/${builder.builderId}`);
              }
            } else {
              await patchUser(user.userId, { builderId: builder.builderId });
              setUser(prev => ({ ...prev, builderId: builder.builderId }));
            }
          }}
        >
          {user.builderId === builder.builderId
            ? 'Remove User from Employee Group'
            : 'Add User to Employee Group'}
        </Button>
        {user.builderId === builder.builderId && (
          <Button
            className="btn-brand-outline btn-compact access-control-btn-danger delete-user-button"
            onClick={handleDeleteUser}
          >
            Delete User Permanently
          </Button>
        )}
      </div>
    </section>
  );
}
